import { GridFilterItem, GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-pro'
import type { InventoryV3ViewMode } from './InventoryV3Footer'

export const INVENTORY_V3_SIDEBARS = ['columns', 'filters', 'templates', 'actions'] as const

export type InventoryV3SidebarTab = (typeof INVENTORY_V3_SIDEBARS)[number]

const VIEW_MODES = new Set<InventoryV3ViewMode>(['active', 'sold', 'pending', 'ready'])
const LAYOUTS = new Set(['list', 'card'])
const PER_PAGE = new Set([20, 50])
const FIELD_RE = /^[A-Za-z0-9_]+$/
const TRUCK_ID_RE = /^[A-Za-z0-9_-]{1,128}$/
const MAX_IDS = 40

/** One truck surface at a time. `add` has no truck. */
export const INVENTORY_V3_OPENS = [
  'add',
  'edit',
  'photos',
  'documents',
  'timeline',
  'notes',
  'fields',
  'website',
] as const

export type InventoryV3Open = (typeof INVENTORY_V3_OPENS)[number]

/** Document dialog tabs, in the same order as the dialog. */
export const INVENTORY_V3_DOC_TABS = ['title', 'purchase', 'payment', 'invoice'] as const

export type InventoryV3DocTab = (typeof INVENTORY_V3_DOC_TABS)[number]

const OPENS = new Set<string>(INVENTORY_V3_OPENS)
const DOC_TABS = new Set<string>(INVENTORY_V3_DOC_TABS)
const SIDEBARS = new Set<string>(INVENTORY_V3_SIDEBARS)
const OPENS_WITH_TRUCK = new Set<InventoryV3Open>([
  'edit',
  'photos',
  'documents',
  'timeline',
  'notes',
  'fields',
  'website',
])

export type InventoryV3RouteState = {
  searchTerms: string[]
  viewMode: InventoryV3ViewMode
  opticOwnedOnly: boolean
  filterModel: GridFilterModel
  page: number
  perPage: number
  /** Null when the link does not choose list or cards. */
  layout: 'list' | 'card' | null
  /** Columns, filters, templates, or actions rail. */
  sidebar: InventoryV3SidebarTab | null
  /** Which truck button is open. */
  open: InventoryV3Open | null
  truckId: string | null
  /** Set only while documents are open. Title is the default and is omitted from the link. */
  docTab: InventoryV3DocTab | null
  /** Checked trucks, for the actions rail. */
  selectedIds: string[]
  /** Rows with the photo strip expanded. */
  expandedIds: string[]
}

const emptyState = (): InventoryV3RouteState => ({
  searchTerms: [],
  viewMode: 'active',
  opticOwnedOnly: false,
  filterModel: { items: [] },
  page: 1,
  perPage: 20,
  layout: null,
  sidebar: null,
  open: null,
  truckId: null,
  docTab: null,
  selectedIds: [],
  expandedIds: [],
})

const positiveInt = (value: string | null, fallback: number): number => {
  if (!value || !/^\d+$/.test(value)) return fallback
  const parsed = Number.parseInt(value, 10)
  return parsed > 0 ? parsed : fallback
}

const truckId = (value: string | null): string | null => {
  if (!value || !TRUCK_ID_RE.test(value)) return null
  return value
}

const truckIds = (values: string[], limit: number): string[] => {
  const seen = new Set<string>()
  const ids: string[] = []
  values.forEach(value => {
    const id = truckId(value)
    if (!id || seen.has(id) || ids.length >= limit) return
    seen.add(id)
    ids.push(id)
  })
  return ids
}

const sanitizeFilterValue = (value: unknown): GridFilterItem['value'] => {
  if (value == null) return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.slice(0, 200)
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map(item => item.slice(0, 200))
      .slice(0, 30)
  }
  return null
}

const parseFilterItem = (raw: string): GridFilterItem | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const record = parsed as Record<string, unknown>
  if (typeof record.field !== 'string' || !FIELD_RE.test(record.field)) return null
  if (typeof record.operator !== 'string' || record.operator.length === 0 || record.operator.length > 32) {
    return null
  }
  return {
    field: record.field,
    operator: record.operator,
    value: sanitizeFilterValue(record.value),
  }
}

const parseOpen = (params: URLSearchParams): Pick<InventoryV3RouteState, 'open' | 'truckId' | 'docTab'> => {
  const openRaw = params.get('open')
  const open = openRaw && OPENS.has(openRaw) ? (openRaw as InventoryV3Open) : null
  const id = truckId(params.get('truck'))
  if (!open) return { open: null, truckId: null, docTab: null }
  if (open === 'add') return { open, truckId: null, docTab: null }
  if (!OPENS_WITH_TRUCK.has(open) || !id) return { open: null, truckId: null, docTab: null }
  const docRaw = params.get('doc')
  const docTab =
    open === 'documents' && docRaw && DOC_TABS.has(docRaw) ? (docRaw as InventoryV3DocTab) : open === 'documents' ? 'title' : null
  return { open, truckId: id, docTab }
}

export const parseInventoryV3Route = (params: URLSearchParams): InventoryV3RouteState => {
  const viewRaw = params.get('view')
  const layoutRaw = params.get('layout')
  const sidebarRaw = params.get('sidebar')
  const perRaw = positiveInt(params.get('per'), 20)
  const items = params
    .getAll('f')
    .map(parseFilterItem)
    .filter((item): item is GridFilterItem => item !== null)
    .slice(0, 20)

  return {
    searchTerms: params
      .getAll('q')
      .map(term => term.trim())
      .filter(Boolean)
      .slice(0, 20),
    viewMode: viewRaw && VIEW_MODES.has(viewRaw as InventoryV3ViewMode) ? (viewRaw as InventoryV3ViewMode) : 'active',
    opticOwnedOnly: params.get('owned') === '1',
    filterModel: {
      items,
      ...(items.length > 0
        ? { logicOperator: params.get('logic') === 'or' ? GridLogicOperator.Or : GridLogicOperator.And }
        : {}),
    },
    page: positiveInt(params.get('page'), 1),
    perPage: PER_PAGE.has(perRaw) ? perRaw : 20,
    layout: layoutRaw && LAYOUTS.has(layoutRaw) ? (layoutRaw as 'list' | 'card') : null,
    sidebar: sidebarRaw && SIDEBARS.has(sidebarRaw as InventoryV3SidebarTab) ? (sidebarRaw as InventoryV3SidebarTab) : null,
    ...parseOpen(params),
    selectedIds: truckIds(params.getAll('sel'), MAX_IDS),
    expandedIds: truckIds(params.getAll('row'), 20),
  }
}

const writeIds = (params: URLSearchParams, key: string, ids: string[], limit: number) => {
  params.delete(key)
  truckIds(ids, limit).forEach(id => params.append(key, id))
}

export const writeInventoryV3Route = (params: URLSearchParams, state: InventoryV3RouteState): void => {
  params.delete('q')
  state.searchTerms.slice(0, 20).forEach(term => {
    const trimmed = term.trim()
    if (trimmed) params.append('q', trimmed)
  })

  if (state.viewMode === 'active') params.delete('view')
  else params.set('view', state.viewMode)

  if (state.opticOwnedOnly) params.set('owned', '1')
  else params.delete('owned')

  if (state.layout === 'list' || state.layout === 'card') params.set('layout', 'card' === state.layout ? 'card' : 'list')
  else params.delete('layout')

  if (state.page > 1) params.set('page', String(state.page))
  else params.delete('page')

  if (state.perPage !== 20 && PER_PAGE.has(state.perPage)) params.set('per', String(state.perPage))
  else params.delete('per')

  params.delete('f')
  params.delete('logic')
  const items = state.filterModel.items
    .filter(item => FIELD_RE.test(item.field) && item.operator)
    .slice(0, 20)
  items.forEach(item => {
    params.append(
      'f',
      JSON.stringify({
        field: item.field,
        operator: item.operator,
        value: item.value ?? null,
      }),
    )
  })
  if (items.length > 0 && state.filterModel.logicOperator === GridLogicOperator.Or) {
    params.set('logic', 'or')
  }

  if (state.sidebar && SIDEBARS.has(state.sidebar)) params.set('sidebar', state.sidebar)
  else params.delete('sidebar')

  const open = state.open && OPENS.has(state.open) ? state.open : null
  const id = truckId(state.truckId)
  if (open === 'add') {
    params.set('open', 'add')
    params.delete('truck')
    params.delete('doc')
  } else if (open && OPENS_WITH_TRUCK.has(open) && id) {
    params.set('open', open)
    params.set('truck', id)
    if (open === 'documents' && state.docTab && state.docTab !== 'title' && DOC_TABS.has(state.docTab)) {
      params.set('doc', state.docTab)
    } else {
      params.delete('doc')
    }
  } else {
    params.delete('open')
    params.delete('truck')
    params.delete('doc')
  }

  writeIds(params, 'sel', state.selectedIds, MAX_IDS)
  writeIds(params, 'row', state.expandedIds, 20)
}

/** Defaults stay out of the query string so a plain inventory link stays short. */
export const inventoryV3RouteSearch = (state: InventoryV3RouteState = emptyState()): string => {
  const params = new URLSearchParams()
  writeInventoryV3Route(params, state)
  return params.toString()
}

/**
 * Apply route patches in order onto a base state.
 * InventoryV3 keeps this in a ref so rapid checkbox + header-filter updates
 * cannot clobber each other when React Router navigations race.
 */
export const mergeInventoryV3RoutePatches = (
  base: InventoryV3RouteState,
  patches: Partial<InventoryV3RouteState>[],
): InventoryV3RouteState =>
  patches.reduce<InventoryV3RouteState>((state, patch) => ({ ...state, ...patch }), base)

/** Stable key for filter-model equality (ignore DataGrid item ids). */
export const inventoryV3FilterModelKey = (model: GridFilterModel): string =>
  JSON.stringify({
    logicOperator: model.logicOperator ?? GridLogicOperator.And,
    items: (model.items ?? []).map(({ field, operator, value }) => ({ field, operator, value })),
  })

/** Header / sidebar “Clear filters” — drops search, Optic-owned, and column filters. */
export const inventoryV3ClearedFiltersPatch = (): Partial<InventoryV3RouteState> => ({
  searchTerms: [],
  opticOwnedOnly: false,
  filterModel: { items: [] },
  page: 1,
})

/** True when this document load is a browser refresh (F5 / reload). */
export const isInventoryV3ReloadNavigation = (): boolean => {
  if (typeof performance === 'undefined') return false
  const entries = performance.getEntriesByType('navigation')
  const nav = entries[0] as PerformanceNavigationTiming | undefined
  if (nav?.type === 'reload') return true
  // Legacy PerformanceNavigation (Safari / older Chromium).
  const legacy = (performance as Performance & { navigation?: { type?: number } }).navigation
  return legacy?.type === 1
}

/** Shareable inventory path. Old `#edit-truck-` links still open through `inventoryHashToRoute`. */
export const inventoryV3Href = (
  input: {
    pathname?: string
    open?: InventoryV3Open | null
    truckId?: string | null
    docTab?: InventoryV3DocTab | null
  } = {},
): string => {
  const state = emptyState()
  state.open = input.open ?? null
  state.truckId = input.truckId ?? null
  state.docTab = input.docTab ?? null
  const search = inventoryV3RouteSearch(state)
  const pathname = input.pathname ?? '/admin/inventory-v3'
  return search ? `${pathname}?${search}` : pathname
}

/** `#add-truck` and `#edit-truck-<id>` become `open` and `truck`. */
export const inventoryHashToRoute = (hash: string): Pick<InventoryV3RouteState, 'open' | 'truckId' | 'docTab'> | null => {
  if (hash === '#add-truck') return { open: 'add', truckId: null, docTab: null }
  if (hash.startsWith('#edit-truck-')) {
    const id = truckId(hash.slice('#edit-truck-'.length))
    if (!id) return null
    return { open: 'edit', truckId: id, docTab: null }
  }
  return null
}

export const inventoryDocumentTabIndex = (tab: InventoryV3DocTab | null): number => {
  if (tab === 'purchase') return 1
  if (tab === 'payment') return 2
  if (tab === 'invoice') return 3
  return 0
}

export const inventoryDocumentTabFromIndex = (index: number): InventoryV3DocTab => {
  if (index === 1) return 'purchase'
  if (index === 2) return 'payment'
  if (index === 3) return 'invoice'
  return 'title'
}
